import React, { useState, useEffect } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import { apiGet, apiPost } from '../data/api';

const mono = { fontFamily: 'JetBrains Mono, monospace' };

const MARKET_LABELS = { match_winner: 'Match Winner', over_under: 'Over/Under', handicap: 'Handicap' };
const KELLY_LABELS = { full: 'Full Kelly', half: 'Half Kelly', quarter: 'Quarter Kelly' };

export default function PlaceBetModal({ isOpen, onClose, onSuccess, matchId, recommendation, matchInfo }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    bankroll_profile_id: '',
    market_type: '',
    selection_name: '',
    odds_taken: '',
    stake_amount: '',
    bookmaker_name: '',
    notes: '',
    line_value: null,
  });

  useEffect(() => {
    if (!isOpen) return;
    apiGet('/tt/bankroll-profiles').then(p => {
      setProfiles(p);
      const def = p.find(pr => pr.is_default) || p[0];
      if (def) {
        setForm(f => ({ ...f, bankroll_profile_id: def.id }));
      }
    }).catch(console.error);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (recommendation) {
      setForm(f => ({
        ...f,
        market_type: recommendation.market_type || '',
        selection_name: recommendation.selection_name || '',
        odds_taken: recommendation.bookmaker_odds?.toString() || '',
        stake_amount: recommendation.recommended_stake_rounded?.toString() || '',
        line_value: recommendation.line_value ?? null,
      }));
    }
  }, [isOpen, recommendation]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        match_id: matchId,
        bankroll_profile_id: form.bankroll_profile_id,
        market_type: form.market_type,
        selection_name: form.selection_name,
        odds_taken: parseFloat(form.odds_taken),
        stake_amount: parseFloat(form.stake_amount),
        bookmaker_name: form.bookmaker_name || null,
        notes: form.notes || null,
        line_value: form.line_value,
        bet_recommendation_id: recommendation?.match_id ? `${recommendation.match_id}_${recommendation.market_type}` : null,
      };
      await apiPost('/tt/placed-bets', payload);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = form.bankroll_profile_id && form.market_type && form.selection_name &&
    parseFloat(form.odds_taken) > 1 && parseFloat(form.stake_amount) > 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="place-bet-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md mx-4 rounded-md overflow-hidden"
        style={{ background: '#18181B', border: '1px solid #3F3F46' }}>

        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid #27272A' }}>
          <h3 className="text-base font-semibold text-zinc-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Segna come Giocata
          </h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300" data-testid="close-modal-btn">
            <X size={18} />
          </button>
        </div>

        {/* Match Info */}
        {matchInfo && (
          <div className="px-4 pt-3 pb-2">
            <div className="text-sm text-zinc-300">{matchInfo.player1} <span className="text-zinc-600">vs</span> {matchInfo.player2}</div>
            <div className="text-[10px] text-zinc-600 mt-0.5">{matchInfo.tournament}</div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {/* Bankroll Profile */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Profilo Bankroll</label>
            <select
              data-testid="select-profile"
              value={form.bankroll_profile_id}
              onChange={e => setForm(f => ({ ...f, bankroll_profile_id: e.target.value }))}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
            >
              {profiles.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.current_bankroll.toFixed(2)}€)</option>
              ))}
            </select>
          </div>

          {/* Market + Selection */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Mercato</label>
              <select
                data-testid="select-market"
                value={form.market_type}
                onChange={e => setForm(f => ({ ...f, market_type: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
              >
                <option value="">—</option>
                <option value="match_winner">Match Winner</option>
                <option value="over_under">Over/Under</option>
                <option value="handicap">Handicap</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Selezione</label>
              <input
                data-testid="input-selection"
                type="text"
                value={form.selection_name}
                onChange={e => setForm(f => ({ ...f, selection_name: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
                placeholder="es. Player 1"
              />
            </div>
          </div>

          {/* Odds + Stake */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Quota Presa</label>
              <input
                data-testid="input-odds"
                type="number" step="0.01" min="1.01"
                value={form.odds_taken}
                onChange={e => setForm(f => ({ ...f, odds_taken: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
                style={mono}
                placeholder="1.85"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Stake (€)</label>
              <input
                data-testid="input-stake"
                type="number" step="0.50" min="0.50"
                value={form.stake_amount}
                onChange={e => setForm(f => ({ ...f, stake_amount: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
                style={mono}
                placeholder="5.00"
              />
            </div>
          </div>

          {/* Bookmaker + Notes */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Bookmaker <span className="text-zinc-600">(opz.)</span></label>
              <input
                data-testid="input-bookmaker"
                type="text"
                value={form.bookmaker_name}
                onChange={e => setForm(f => ({ ...f, bookmaker_name: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
                placeholder="es. Bet365"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Note <span className="text-zinc-600">(opz.)</span></label>
              <input
                data-testid="input-notes"
                type="text"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-emerald-500/50"
                placeholder=""
              />
            </div>
          </div>

          {/* Recommendation info */}
          {recommendation && (
            <div className="rounded-sm p-2.5 text-[10px] space-y-0.5" style={{ background: '#09090B', border: '1px solid #27272A' }}>
              <div className="text-zinc-500 uppercase tracking-wider font-medium mb-1">Da raccomandazione Kelly</div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Mercato</span>
                <span className="text-zinc-300">{MARKET_LABELS[recommendation.market_type] || recommendation.market_type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">EV</span>
                <span className="text-emerald-400" style={mono}>+{recommendation.ev_pct?.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Kelly Mode</span>
                <span className="text-zinc-300">{KELLY_LABELS[recommendation.recommended_kelly_mode] || recommendation.recommended_kelly_mode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Fair Odds</span>
                <span className="text-zinc-300" style={mono}>{recommendation.fair_odds?.toFixed(3)}</span>
              </div>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            data-testid="submit-bet-btn"
            className={`w-full py-2.5 rounded-sm text-sm font-semibold transition-colors flex items-center justify-center gap-2 ${
              canSubmit && !submitting
                ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
            }`}
          >
            {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Conferma Giocata
          </button>
        </form>
      </div>
    </div>
  );
}
